import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    IconButton,
    TextField,
} from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { useCustomStyles, useDispatchSuccessNotification } from '../../hooks';
import { ApiTokenInterface } from '../../types/ApiKey.interface';
import { copyToClipboard } from '../../utils';

const ApiTokenDialog = ({
    apiToken,
    showDialog,
    onClose,
    dialogProps,
    closeOnBackdropClick,
}: {
    apiToken: ApiTokenInterface;
    showDialog: boolean;
    onClose: any;
    dialogProps?: DialogProps;
    closeOnBackdropClick?: boolean;
}) => {
    const { t } = useTranslation();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const classes = useCustomStyles();
    const handleClose = useCallback(
        (event: any, reason: string) => {
            if (!closeOnBackdropClick && reason === 'backdropClick') return;
            onClose();
        },
        [onClose, closeOnBackdropClick]
    );

    const handleCopy = useCallback(async () => {
        await copyToClipboard(apiToken.access_token);
        dispatchSuccessNotification({ message: t('apiKey.tokenCopied') });
    }, [apiToken.access_token, dispatchSuccessNotification, t]);

    return (
        <Dialog {...dialogProps} open={showDialog} onClose={handleClose}>
            <DialogTitle sx={{ m: 0, p: 2 }}>{t('apiKey.token')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => onClose()}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent style={{ paddingTop: '1rem' }}>
                <TextField
                    sx={{
                        '& .MuiInputBase-input': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                        },
                    }}
                    label={t('field.accessToken')}
                    fullWidth
                    value={apiToken.access_token}
                    multiline
                    maxRows={3}
                    helperText={t('apiKey.tokenHelper', {
                        hours: Math.floor(apiToken.expires_in) / 60 / 60,
                    })}
                />
            </DialogContent>
            <DialogActions sx={{ ...classes.dialogActions, pb: 3 }}>
                <Button onClick={() => onClose()} variant="outlined">
                    {t('action.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleCopy}>
                    {t('action.copyToClipboard')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ApiTokenDialog;
